import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchArtist } from '../api/public'
import { CmsImage } from '../components/CmsImage'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { cmsImageSocialUrl } from '../lib/cms-image'
import {
  schemaBreadcrumb,
  schemaGraph,
  schemaMusicAlbum,
  schemaPersonArtist,
  schemaWebPage,
} from '../lib/schema-org'
import { spotifyEmbedSrc } from '../lib/spotify-embed'

export function ArtistDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const q = useQuery({
    queryKey: ['artist', slug],
    queryFn: () => fetchArtist(slug!),
    enabled: !!slug,
  })

  const artist = q.data
  const path = slug ? `/artists/${slug}` : '/artists'
  useCrtPageTitle(artist?.name ?? null)

  const jsonLd = useMemo(() => {
    if (!artist || !slug) return null
    const crumbs = schemaBreadcrumb(
      [
        { name: 'Home', path: '/' },
        { name: 'Artists', path: '/artists' },
        { name: artist.name, path },
      ],
      path,
    )
    const person = schemaPersonArtist(artist, path)
    const albums = (artist.releases ?? []).map((r) => schemaMusicAlbum(r, artist.name))
    const page = schemaWebPage({
      path,
      name: artist.name,
      description: artist.bioHtml ? undefined : `${artist.name} — BNKR Artist.`,
      pageType: 'ProfilePage',
      image: artist.portraitImageUrl,
      breadcrumb: crumbs,
      mainEntity: { '@id': person['@id'] as string },
    })
    return schemaGraph(crumbs, page, person, ...albums)
  }, [artist, slug, path])

  usePageMeta(
    artist
      ? {
          title: artist.name,
          description: `${artist.name} — Artist bei BNKR.`,
          canonical: path,
          ogImage: cmsImageSocialUrl(artist.portraitImageUrl) ?? artist.portraitImageUrl,
          jsonLd,
        }
      : null,
  )

  const embed = q.data ? spotifyEmbedSrc(q.data.spotifyUrl) : null
  const tracks = q.data?.tracksJson ?? []

  return (
    <>
      <section className="section" style={{ paddingBottom: 'var(--sp-16)', paddingTop: 'var(--sp-4)' }}>
        <div className="wrap">
          {q.isError && <p className="error">Artist nicht gefunden.</p>}
          {q.data ? (
            <>
              <div id="artist-detail">
                <div className="kicker crt">↳ /artists/{q.data.slug}</div>
                <h1 className="display h2" style={{ marginTop: 14 }}>
                  {q.data.name}
                </h1>

                <div className="artist-detail-hero" style={{ marginTop: 40 }}>
                  {q.data.portraitImageUrl ? (
                    <div className="artist-detail-hero__portrait">
                      <CmsImage src={q.data.portraitImageUrl} size="lg" alt="" loading="eager" width={640} height={853} />
                    </div>
                  ) : null}
                  <div>
                    <div className="artist-social-row">
                      {q.data.websiteUrl ? (
                        <a className="lnk" href={q.data.websiteUrl} target="_blank" rel="noreferrer">
                          Website ↗
                        </a>
                      ) : null}
                      {q.data.spotifyUrl ? (
                        <a className="lnk" href={q.data.spotifyUrl} target="_blank" rel="noreferrer">
                          Spotify ↗
                        </a>
                      ) : null}
                      {q.data.soundcloudUrl ? (
                        <a className="lnk" href={q.data.soundcloudUrl} target="_blank" rel="noreferrer">
                          SoundCloud ↗
                        </a>
                      ) : null}
                      {q.data.bandcampUrl ? (
                        <a className="lnk" href={q.data.bandcampUrl} target="_blank" rel="noreferrer">
                          Bandcamp ↗
                        </a>
                      ) : null}
                      {q.data.mixcloudUrl ? (
                        <a className="lnk" href={q.data.mixcloudUrl} target="_blank" rel="noreferrer">
                          Mixcloud ↗
                        </a>
                      ) : null}
                    </div>
                    {embed ? (
                      <iframe
                        title="Spotify"
                        style={{ border: 0, borderRadius: 8, width: '100%', maxWidth: 420, height: 152, marginTop: 24 }}
                        src={embed}
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                      />
                    ) : null}
                    {q.data.bioHtml ? (
                      <div className="rich-text rich-text--paper" style={{ marginTop: 'var(--sp-8)' }} dangerouslySetInnerHTML={{ __html: q.data.bioHtml }} />
                    ) : null}
                  </div>
                </div>
              </div>

              {tracks.length > 0 ? (
                <div style={{ marginTop: 'var(--sp-12)' }}>
                  <div className="kicker crt" style={{ marginBottom: 14 }}>
                    Tracks
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontFamily: 'var(--mono)', fontSize: 14 }}>
                    {tracks.map((t, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 16,
                          flexWrap: 'wrap',
                          padding: '12px 0',
                          borderBottom: '1px solid var(--line)',
                        }}
                      >
                        <span>{t.title}</span>
                        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {t.url ? (
                            <a className="lnk" href={t.url} target="_blank" rel="noreferrer">
                              Link ↗
                            </a>
                          ) : null}
                          {t.spotifyUrl ? (
                            <a className="lnk" href={t.spotifyUrl} target="_blank" rel="noreferrer">
                              Spotify ↗
                            </a>
                          ) : null}
                          {t.soundcloudUrl ? (
                            <a className="lnk" href={t.soundcloudUrl} target="_blank" rel="noreferrer">
                              SC ↗
                            </a>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {q.data.releases?.length ? (
                <div style={{ marginTop: 'var(--sp-12)' }}>
                  <div className="sect-head" style={{ marginBottom: 24 }}>
                    <div className="kicker crt">↳ Releases</div>
                  </div>
                  <div className="release-grid">
                    {q.data.releases.map((r) => (
                      <article key={r.id} className="release">
                        <div className="art">
                          {r.coverImageUrl ? (
                            <CmsImage
                              src={r.coverImageUrl}
                              size="thumb"
                              alt=""
                              width={400}
                              height={400}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          ) : null}
                        </div>
                        <div className="body">
                          <h4>{r.title}</h4>
                          <div className="links">
                            {r.spotifyUrl ? (
                              <a className="lnk" href={r.spotifyUrl} target="_blank" rel="noreferrer">
                                Spotify ↗
                              </a>
                            ) : null}
                            {r.soundcloudUrl ? (
                              <a className="lnk" href={r.soundcloudUrl} target="_blank" rel="noreferrer">
                                SoundCloud ↗
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ) : null}

              {q.data.relatedArtists?.length ? (
                <div style={{ marginTop: 'var(--sp-12)' }}>
                  <div className="kicker" style={{ marginBottom: 14 }}>
                    Verbindungen
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {q.data.relatedArtists.map((rel) => (
                      <Link key={rel.id} className="lnk" to={`/artists/${rel.slug}`}>
                        {rel.relationLabel ? `${rel.relationLabel}: ` : ''}
                        {rel.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </>
  )
}
